import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useDocumentTitle } from "../../../../utils";
import { OppBotContext, useOppBotQuery } from "./hooks/useOppBotApi";
import HelpDrawer from "./components/HelpDrawer";
import { CopyButton } from "./components/Ui";
import InboxPage from "./pages/InboxPage";
import EmailDetailPage from "./pages/EmailDetailPage";

// The app shell forces the template's dark theme; switch the body to light while mounted.
function useLightTheme() {
  useEffect(() => {
    const body = document.body;
    const previous = body.getAttribute("data-theme-version");
    body.setAttribute("data-theme-version", "light");
    body.setAttribute("data-oppbot", "true");
    return () => {
      if (previous) body.setAttribute("data-theme-version", previous);
      else body.removeAttribute("data-theme-version");
      body.removeAttribute("data-oppbot");
    };
  }, []);
}

export default function OppBot() {
  useDocumentTitle("Opp Bot");
  useLightTheme();
  const [version, setVersion] = useState(0);
  const bump = useCallback(() => setVersion((v) => v + 1), []);
  return (
    <ProviderShell version={version} bump={bump} />
  );
}

function ProviderShell({ version, bump }) {
  // meta is fetched outside the versioned context so approving an email does not refetch picklists
  const baseCtx = useMemo(() => ({ version: 0, bump: () => {}, meta: null }), []);
  return (
    <OppBotContext.Provider value={baseCtx}>
      <MetaLoader version={version} bump={bump} />
    </OppBotContext.Provider>
  );
}

function MetaLoader({ version, bump }) {
  const metaParams = useMemo(() => ({}), []);
  const meta = useOppBotQuery("meta", metaParams);
  const ctx = useMemo(() => ({ version, bump, meta: meta.data, metaLoading: meta.loading, refetchMeta: meta.refetch }), [version, bump, meta.data, meta.loading, meta.refetch]);
  return (
    <OppBotContext.Provider value={ctx}>
      <Shell />
    </OppBotContext.Provider>
  );
}

function Shell() {
  const location = useLocation();
  const [helpOpen, setHelpOpen] = useState(false);
  const ctx = React.useContext(OppBotContext);
  const meta = ctx.meta;
  const isDetail = /\/landmark\/oppbot\/email\//.test(location.pathname);
  const isLocalDev = ["localhost", "127.0.0.1"].includes(window.location.hostname);

  return (
    <div className="ob-wrapper">
      <header className="ob-topbar">
        <div className={`ob-topbar__inner ${isLocalDev ? "ob-topbar__inner--dev-offset" : ""}`}>
          <div className="ob-topbar__left">
            <Link to="/landmark/oppbot" className="ob-brand" aria-label="Opp Bot home">
              <span className="ob-brand__mark">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M4 4h16v12H7l-3 3z" />
                  <path d="M9 9h6M9 12h4" />
                </svg>
              </span>
              <span className="ob-brand__name">Opp Bot</span>
            </Link>
            <nav className="ob-crumbs" aria-label="Breadcrumb">
              <Link to="/landmark/oppbot" className={isDetail ? "" : "is-current"}>
                Inbox
              </Link>
              {isDetail && (
                <>
                  <span className="ob-crumbs__sep">/</span>
                  <span className="is-current">Email</span>
                </>
              )}
            </nav>
          </div>
          <div className="ob-topbar__right">
            {meta && meta.dryRun && (
              <span className="ob-pill ob-pill--warning ob-pill--sm" title="OPPBOT_DRY_RUN is on - nothing is written to Salesforce without approval">
                Dry run
              </span>
            )}
            {meta && meta.inboundAddress && (
              <div className="ob-address-chip" title="Forward emails here">
                <span className="ob-address-chip__label">Forward to</span>
                <span className="ob-address-chip__value">{meta.inboundAddress}</span>
                <CopyButton text={meta.inboundAddress} label="Copy" />
              </div>
            )}
            <button type="button" className="ob-btn ob-btn--sm" onClick={() => setHelpOpen(true)}>
              How to use
            </button>
          </div>
        </div>
      </header>

      <main className="ob-main">
        <Routes>
          <Route index element={<InboxPage />} />
          <Route path="email/:id" element={<EmailDetailPage />} />
          <Route path="*" element={<Navigate to="/landmark/oppbot" replace />} />
        </Routes>
      </main>

      <footer className="ob-footer">
        <span>
          Opp Bot reads forwarded BD emails with {meta ? meta.model : "OpenAI"} and writes to Salesforce
          {meta ? ` when confidence ≥ ${Math.round(meta.autoApplyMinConfidence * 100)}%` : ""}. Everything it does is logged here.
        </span>
        {meta && <span>Prompt v{meta.promptVersion}</span>}
      </footer>

      <HelpDrawer open={helpOpen} onClose={() => setHelpOpen(false)} meta={meta} />
    </div>
  );
}
